import React, { useEffect, useMemo, useState, useRef } from "react";
import "./Clients.css";
import SidePanel from "./SidePanel";
import { apiFetch } from "../utils/api";
import { canDo } from "../utils/permissions";
import Paginator from "./Paginator";

const emptyForm = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  birthday: "",
  clientResource: "",
  referredBy: "",
  additionalEmails: "",
  occupation: "",
  additionalPhone: "",
  country: "",
  address: "",
  branchPublicId: "",
  notifyEmail: true,
  notifyWhatsapp: false,
  notifyText: false
};

const Clients = ({ currentUser }) => {
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [activePanel, setActivePanel] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState([]);
  const [clientWhatsAppHistory, setClientWhatsAppHistory] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [formError, setFormError] = useState("");
  const [whatsappNotice, setWhatsappNotice] = useState(null);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const submitInFlightRef = useRef(false);
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsPageSize, setClientsPageSize] = useState(25);

  const panelOpen = activePanel !== null;
  const defaultBranchPublicId = branches.length === 1 ? String(branches[0]?.publicId || "") : "";
  const defaultAllowedBranchPublicId = currentUser?.allowedBranchPublicIds?.length === 1
    ? String(currentUser.allowedBranchPublicIds[0] || "")
    : "";

  const formatDate = value => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const formatCurrency = value => {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) return "0.00";
    return amount.toFixed(2);
  };

  const safeTrim = value => String(value ?? "").trim();
  const normalizeMobile = value => String(value || "").replace(/[\s()-]/g, "").trim();
  const normalizeClientForm = client => ({
    ...emptyForm,
    ...client,
    firstName: String(client?.firstName ?? ""),
    lastName: String(client?.lastName ?? ""),
    mobile: String(client?.mobile ?? ""),
    email: String(client?.email ?? ""),
    birthday: String(client?.birthday ?? ""),
    clientResource: String(client?.clientResource ?? ""),
    referredBy: String(client?.referredBy ?? ""),
    additionalEmails: String(client?.additionalEmails ?? ""),
    occupation: String(client?.occupation ?? ""),
    additionalPhone: String(client?.additionalPhone ?? ""),
    country: String(client?.country ?? ""),
    address: String(client?.address ?? ""),
    branchPublicId: String(client?.branchPublicId ?? ""),
    notifyEmail: !!client?.notifyEmail,
    notifyWhatsapp: !!client?.notifyWhatsapp,
    notifyText: !!client?.notifyText
  });

  useEffect(() => {
    const path = branchFilter
      ? `/api/clients?branchPublicId=${encodeURIComponent(branchFilter)}`
      : "/api/clients";
    apiFetch(path)
      .then(res => res.json())
      .then(data => setClients(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to load clients", err));
  }, [branchFilter]);

  useEffect(() => {
    apiFetch("/api/branches")
      .then(res => res.json())
      .then(data => setBranches(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!branches.length) return;
    setBranchFilter(prev => {
      if (prev) return prev;
      if (branches.length === 1) return String(branches[0].publicId);
      if (defaultAllowedBranchPublicId) return defaultAllowedBranchPublicId;
      return prev;
    });
  }, [branches, defaultAllowedBranchPublicId]);

  useEffect(() => {
    if (!defaultBranchPublicId) return;
    setForm(prev => (prev.branchPublicId ? prev : { ...prev, branchPublicId: defaultBranchPublicId }));
  }, [defaultBranchPublicId]);

  const visibleClients = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.toLowerCase();
    return clients.filter(c => {
      const fullName = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      return (
        fullName.includes(q) ||
        (c.mobile || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
      );
    });
  }, [clients, search]);

  useEffect(() => { setClientsPage(1); }, [visibleClients]);

  const pagedClients = useMemo(
    () => visibleClients.slice((clientsPage - 1) * clientsPageSize, clientsPage * clientsPageSize),
    [visibleClients, clientsPage, clientsPageSize]
  );

  const branchByPublicId = useMemo(() => {
    const map = new Map();
    branches.forEach(branch => {
      const publicId = String(branch.publicId || branch.id || "");
      if (publicId) map.set(publicId, branch);
    });
    return map;
  }, [branches]);

  const openCreate = () => {
    setEditingId(null);
    setFormError("");
    setForm({
      ...emptyForm,
      branchPublicId: defaultBranchPublicId
    });
    setActivePanel("form");
  };

  const openEdit = client => {
    setEditingId(client.publicId || String(client.id));
    setFormError("");
    setForm(normalizeClientForm(client));
    setActivePanel("form");
  };

  const openDetails = async client => {
    const ref = client.publicId || String(client.id);
    setActivePanel("details");
    setDetailsLoading(true);
    setDetailsError("");
    setSelectedClient(client);
    setClientHistory([]);
    setClientWhatsAppHistory([]);

    try {
      const res = await apiFetch(`/api/clients/${ref}/details`);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      setSelectedClient(data.client || client);
      setClientHistory(Array.isArray(data.history) ? data.history : []);
      setClientWhatsAppHistory(Array.isArray(data.whatsappHistory) ? data.whatsappHistory : []);
    } catch (err) {
      console.error("Failed to load client details", err);
      setDetailsError("Could not load client details and history.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const closePanel = () => {
    setActivePanel(null);
    setEditingId(null);
    setSelectedClient(null);
    setClientHistory([]);
    setClientWhatsAppHistory([]);
    setDetailsError("");
    setFormError("");
    setForm(emptyForm);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitInFlightRef.current || isSaving) return;
    
    submitInFlightRef.current = true;
    setIsSaving(true);
    
    setFormError("");

    const firstName = safeTrim(form.firstName);
    const mobile = safeTrim(form.mobile);
    const branchPublicId = safeTrim(form.branchPublicId);

    if (!firstName || !mobile) {
      setFormError("First name and mobile number are required.");
      submitInFlightRef.current = false;
      setIsSaving(false);
      return;
    }

    if (!branchPublicId) {
      setFormError("Branch is required.");
      submitInFlightRef.current = false;
      setIsSaving(false);
      return;
    }

    const normalizedInputMobile = normalizeMobile(mobile);
    const duplicateClient = clients.find(c => {
      const currentRef = c.publicId || String(c.id);
      const editingRef = editingId ? String(editingId) : null;
      if (editingRef && String(currentRef) === editingRef) return false;
      return normalizeMobile(c.mobile) === normalizedInputMobile;
    });
    if (duplicateClient) {
      setFormError("Mobile number already exists. Please use a different mobile number.");
      submitInFlightRef.current = false;
      setIsSaving(false);
      return;
    }

    const payload = {
      ...form,
      firstName,
      lastName: safeTrim(form.lastName),
      mobile,
      email: safeTrim(form.email),
      address: safeTrim(form.address),
      branchPublicId
    };

    try {
      let saved;
      if (editingId) {
        const res = await apiFetch(`/api/clients/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Server error ${res.status}`);
        }
        saved = await res.json();
        setClients(prev => prev.map(c => (String(c.publicId || c.id) === String(editingId) ? saved : c)));
      } else {
        const res = await apiFetch("/api/clients", {
          method: "POST",
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Server error ${res.status}`);
        }
        saved = await res.json();
        setClients(prev => [saved, ...prev]);
        if (saved?.whatsappNotification?.attempted) {
          setWhatsappNotice(saved.whatsappNotification);
        }
      }
      closePanel();
      submitInFlightRef.current = false;
      setIsSaving(false);
    } catch (err) {
      console.error("Failed to save client", err);
      setFormError(err.message || "Could not save client");
      submitInFlightRef.current = false;
      setIsSaving(false);
    }
  };

  const deleteClient = async id => {
    if (!window.confirm("Delete this client?")) return;
    try {
      const res = await apiFetch(`/api/clients/${id}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error("Delete failed");
      setClients(prev => prev.filter(c => String(c.publicId || c.id) !== String(id)));
      if (String(editingId || "") === String(id)) closePanel();
    } catch (err) {
      console.error("Failed to delete client", err);
      alert("Could not delete client");
    }
  };

  return (
    <div className="clients-page page main-content">
      {whatsappNotice && (
        <div className={`client-whatsapp-notice ${whatsappNotice.status || ""}`.trim()}>
          <span>{whatsappNotice.message}</span>
          <button type="button" onClick={() => setWhatsappNotice(null)} aria-label="Dismiss">&times;</button>
        </div>
      )}
      <div className="clients-header">
        <div>
          <h1>Clients</h1>
          <p>Track customer profiles, contact details, and branch-linked visit history.</p>
        </div>
        <div className="clients-actions">
          {branches.length !== 1 && (
            <select
              className="client-branch-filter"
              value={branchFilter}
              onChange={e => setBranchFilter(e.target.value)}
            >
              <option value="">All branches</option>
              {branches.map(b => (
                <option key={b.publicId || b.id} value={b.publicId || ""}>
                  {b.branchCode ? `${b.branchCode} - ` : ""}{b.branchName || "Branch"}
                </option>
              ))}
            </select>
          )}
          <input
            className="client-search-input"
            type="text"
            placeholder="Search by name, mobile or email"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {canDo(currentUser, "clients", "add") && (
            <button className="open-panel-btn" onClick={openCreate}>Add Client</button>
          )}
        </div>
      </div>

      <div className="clients-list">
        {visibleClients.length === 0 ? (
          <p>No clients found.</p>
        ) : (
          <table className="clients-table">
            <thead>
              <tr>
                <th>Branch Code</th>
                <th>Client</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Birthday</th>
                <th className="num-cell">Total Sales</th>
                <th>Last Visited</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedClients.map((client, idx) => (
                <tr key={client.publicId || client.id || idx} onClick={() => openDetails(client)}>
                  <td data-label="Branch Code">{branchByPublicId.get(String(client.branchPublicId || ""))?.branchCode || "-"}</td>
                  <td data-label="Client">{`${client.firstName || ""} ${client.lastName || ""}`.trim() || "-"}</td>
                  <td data-label="Mobile">{client.mobile || "-"}</td>
                  <td data-label="Email">{client.email || "-"}</td>
                  <td data-label="Birthday">{formatDate(client.birthday)}</td>
                  <td data-label="Total Sales" className="num-cell">{formatCurrency(client.totalSales)}</td>
                  <td data-label="Last Visited">{formatDate(client.lastVisitedDate)}</td>
                  <td data-label="Actions" className="actions-cell">
                    {canDo(currentUser, "clients", "edit") && (
                    <button
                      className="icon-btn edit-btn"
                      title="Edit"
                      onClick={e => {
                        e.stopPropagation();
                        openEdit(client);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="#fff" opacity="0.9"/>
                        <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="#fff" opacity="0.9"/>
                      </svg>
                    </button>
                    )}
                    {canDo(currentUser, "clients", "delete") && (
                    <button
                      className="icon-btn delete-btn"
                      title="Delete"
                      onClick={e => {
                        e.stopPropagation();
                        deleteClient(client.publicId || String(client.id));
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z" fill="#fff" opacity="0.95"/>
                        <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#fff" opacity="0.95"/>
                      </svg>
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginator
          page={clientsPage}
          pageSize={clientsPageSize}
          total={visibleClients.length}
          onPageChange={setClientsPage}
          onPageSizeChange={size => { setClientsPageSize(size); setClientsPage(1); }}
        />
      </div>

      <SidePanel
        isOpen={panelOpen}
        onClose={closePanel}
        className={activePanel === "form" ? "panel-medium clients-form-panel" : "panel-medium clients-details-panel"}
      >
        {activePanel === "form" ? (
          <div className="client-form-container">
            <h2>{editingId ? "Edit Client" : "Add New Client"}</h2>

            <form className="client-form" onSubmit={handleSubmit}>
              <div className="form-section-title">Basic Details</div>

              <div className="client-grid two-col">
                <input name="firstName" placeholder="First name *" value={form.firstName} onChange={handleChange} required />
                <input name="lastName" placeholder="Last name" value={form.lastName} onChange={handleChange} />
              </div>

              <div className="client-grid two-col">
                <input name="mobile" placeholder="Mobile number *" value={form.mobile} onChange={handleChange} required />
                <input type="email" name="email" placeholder="Email" value={form.email} onChange={handleChange} />
              </div>

              <div className="client-grid two-col">
                <input type="date" name="birthday" placeholder="Birthday" value={form.birthday} onChange={handleChange} />
                <input name="occupation" placeholder="Occupation" value={form.occupation} onChange={handleChange} />
              </div>

              <div className="form-section-title">Sales and Visit Summary</div>
              <div className="client-grid three-col client-stats-grid">
                <div className="readonly-field">
                  <label>Total Sales</label>
                  <input value={formatCurrency(form.totalSales)} readOnly />
                </div>
                <div className="readonly-field">
                  <label>Created Date</label>
                  <input value={formatDate(form.createdAt)} readOnly />
                </div>
                <div className="readonly-field">
                  <label>Last Visited</label>
                  <input value={formatDate(form.lastVisitedDate)} readOnly />
                </div>
              </div>
              {(form.createdBy || form.updatedBy || form.updatedAt) && (
                <div className="audit-trail">
                  {form.createdBy && (
                    <span className="audit-item"><strong>Created by:</strong> {form.createdBy}</span>
                  )}
                  {(form.updatedBy || form.updatedAt) && (
                    <span className="audit-item"><strong>Updated by:</strong> {form.updatedBy || "\u2014"}{form.updatedAt ? ` \u00b7 ${new Date(form.updatedAt).toLocaleString()}` : ""}</span>
                  )}
                </div>
              )}

              <div className="form-section-title">Additional Info</div>

              <div className="client-grid two-col">
                <select name="branchPublicId" value={form.branchPublicId} onChange={handleChange} required>
                  {!defaultBranchPublicId && <option value="">Select branch *</option>}
                  {branches.map(b => (
                    <option key={b.publicId || b.id} value={b.publicId || b.id}>
                      {b.branchName}{b.branchCode ? ` (${b.branchCode})` : ""}
                    </option>
                  ))}
                </select>
                <span />
              </div>

              <div className="client-grid two-col">
                <input name="clientResource" placeholder="Client resource (Instagram, Walk-in, etc.)" value={form.clientResource} onChange={handleChange} />
                <input name="referredBy" placeholder="Referred by" value={form.referredBy} onChange={handleChange} />
              </div>

              <div className="client-grid two-col">
                <input name="additionalEmails" placeholder="Additional emails (comma separated)" value={form.additionalEmails} onChange={handleChange} />
                <input name="country" placeholder="Country" value={form.country} onChange={handleChange} />
              </div>

              <input name="additionalPhone" placeholder="Additional phone number" value={form.additionalPhone} onChange={handleChange} />

              <textarea
                name="address"
                placeholder="Address"
                value={form.address}
                onChange={handleChange}
              ></textarea>

              <div className="form-section-title">Basic Settings</div>
              <div className="notify-box">
                <label className="notify-option"><input type="checkbox" name="notifyEmail" checked={!!form.notifyEmail} onChange={handleChange} /><span>Email notifications</span></label>
                <label className="notify-option"><input type="checkbox" name="notifyWhatsapp" checked={!!form.notifyWhatsapp} onChange={handleChange} /><span>WhatsApp notifications</span></label>
                <label className="notify-option"><input type="checkbox" name="notifyText" checked={!!form.notifyText} onChange={handleChange} /><span>Text message notifications</span></label>
              </div>

              {formError && <p className="client-form-error">{formError}</p>}

              <div className="client-form-actions">
                {(canDo(currentUser, "clients", "add") || (editingId && canDo(currentUser, "clients", "edit"))) && (
                  <button type="submit" disabled={isSaving}>{isSaving ? "Saving..." : editingId ? "Save Client" : "Create Client"}</button>
                )}
                {editingId && canDo(currentUser, "clients", "delete") && (
                  <button
                    type="button"
                    className="client-delete-btn"
                    onClick={() => deleteClient(editingId)}
                  >
                    Delete Client
                  </button>
                )}
              </div>
            </form>
          </div>
        ) : (
          <div className="client-details-panel">
            <h2>Client Details and History</h2>

            {detailsLoading ? (
              <p>Loading client details...</p>
            ) : detailsError ? (
              <p className="client-form-error">{detailsError}</p>
            ) : !selectedClient ? (
              <p>Select a client to view details.</p>
            ) : (
              <>
                <div className="client-details-header">
                  <h3>{`${selectedClient.firstName || ""} ${selectedClient.lastName || ""}`.trim() || "Unnamed Client"}</h3>
                  <p>{selectedClient.mobile || "No mobile"}</p>
                  <p>{selectedClient.email || "No email"}</p>
                </div>

                <div className="client-details-grid">
                  <div className="detail-card">
                    <span>Total Sales</span>
                    <strong>{formatCurrency(selectedClient.totalSales)}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Created Date</span>
                    <strong>{formatDate(selectedClient.createdAt)}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Last Visited</span>
                    <strong>{formatDate(selectedClient.lastVisitedDate)}</strong>
                  </div>
                </div>

                <div className="history-section">
                  <h4>Sales History</h4>
                  {clientHistory.length === 0 ? (
                    <p>No history found for this client.</p>
                  ) : (
                    <div className="history-list">
                      {clientHistory.map((sale, index) => (
                        <div className="history-row" key={sale.publicId || sale.id || index}>
                          <div>
                            <strong>{sale.invoiceNo || "Invoice"}</strong>
                            <p>{formatDate(sale.saleDate || sale.createdAt)}</p>
                          </div>
                          <div>
                            <strong>{formatCurrency(sale.totalAmount)}</strong>
                            <p>{sale.paymentMethod || "-"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="history-section">
                  <h4>WhatsApp History</h4>
                  {clientWhatsAppHistory.length === 0 ? (
                    <p>No WhatsApp messages found for this client.</p>
                  ) : (
                    <div className="history-list">
                      {clientWhatsAppHistory.map((message, index) => {
                        const outbound = String(message.direction || "").toLowerCase() === "outbound";
                        return (
                          <div className={`history-row whatsapp-row ${outbound ? "outbound" : "inbound"}`.trim()} key={message.publicId || message.id || index}>
                            <div>
                              <strong>{outbound ? "Outgoing" : "Incoming"}</strong>
                              <p>{message.messageText || (message.status ? `Status: ${message.status}` : "(no content)")}</p>
                            </div>
                            <div>
                              <strong>{message.status || "-"}</strong>
                              <p>{formatDate(message.eventTimestamp || message.createdAt)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </SidePanel>
    </div>
  );
};

export default Clients;
